import { useForm } from '@nerdgeschoss/react-use-form-library';
import React from 'react';

import { Button } from '../../../components/Button/Button';
import { Card } from '../../../components/Card/Card';
import { ContributionListChart } from '../../../components/Charts/contribution_list_chart';
import { FormSelect } from '../../../components/Form/Select/FormSelect';
import { NoData } from '../../../components/NoData/NoData';
import { Spinner } from '../../../components/Spinner/Spinner';
import { Stack } from '../../../components/Stack/Stack';
import { Text } from '../../../components/text/text';
import { graphql } from '../../../generated';
import { SalesOverTimePeriod, StatisticsCountriesStatistic } from '../../../generated/graphql';
import { t } from '../../../locales/translate';
import { formatCurrency, formatNumber } from '../../../util/formatters';
import { withSuspendedCard } from '../../../util/suspense';
import { useSuspenseQuery } from '../../../util/use_suspense_query';

const DASHBOARD_COUNTRIES = graphql(`
  query DashboardCountries(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $statistic: StatisticsCountriesStatistic
    $after: String
    $first: Int
  ) {
    statistics {
      countries(during: { from: $from, to: $to }, statistic: $statistic, first: $first, after: $after) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            absolute
            country {
              name
            }
            percentage
          }
        }
      }
    }
  }
`);

interface Props {
  from: Date;
  to: Date;
  period: SalesOverTimePeriod;
}

function Countries$({ from, to }: Props): JSX.Element | null {
  const { model, fields } = useForm<{
    statistic: StatisticsCountriesStatistic;
  }>({
    model: {
      statistic: 'TURNOVER',
    },
  });

  const { data, loadingMore, fetchMore } = useSuspenseQuery(DASHBOARD_COUNTRIES, {
    variables: {
      from,
      to,
      statistic: model.statistic,
      first: 5,
    },
  });

  const countries = data.statistics?.countries.edges.map((e) => e.node) || [];
  const chartData =
    countries?.map((item) => ({
      percentage: item.percentage,
      label: item.country?.name || t.component.countries.noCountry,
      value: model.statistic === 'TURNOVER' ? formatCurrency(item.absolute) : formatNumber(item.absolute, 0),
    })) || [];

  const statisticMap = {
    TURNOVER: 'turnover',
    ORDER_COUNT: 'orderCount',
    SESSION_COUNT: 'sessionCount',
  } as const;

  return (
    <Card
      size="dashboard"
      title={<p>{t.component.countries.title}</p>}
      actions={
        <FormSelect
          {...fields.statistic}
          fullBorder
          icon="filter"
          options={[
            {
              value: 'TURNOVER',
              label: t.component.countries.turnover,
            },
            {
              value: 'ORDER_COUNT',
              label: t.component.countries.orderCount,
            },
            {
              value: 'SESSION_COUNT',
              label: t.component.countries.sessionCount,
            },
          ]}
          name="statistic"
        />
      }
      footer={
        <Stack fullWidth justify="center">
          {countries?.length && loadingMore ? (
            <Spinner />
          ) : (
            data.statistics?.countries.pageInfo.hasNextPage && (
              <Button
                onClick={() => {
                  fetchMore({
                    variables: {
                      after: data?.statistics?.countries.pageInfo.endCursor,
                    },
                  });
                }}
                type="secondary-dark"
                title={t.actions.loadMore}
              />
            )
          )}
        </Stack>
      }
    >
      {countries?.length ? (
        <Stack size="md">
          <Text type="dashboard-card-label">{t.component.countries[statisticMap[model.statistic]]}</Text>
          <ContributionListChart data={chartData} />
        </Stack>
      ) : (
        <NoData description={t.component.countries.noData} />
      )}
    </Card>
  );
}

export const Countries = withSuspendedCard(Countries$, {
  loadingTitle: t.component.countries.title,
  loadingHeight: 452,
});
